import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  catchError,
  defaultIfEmpty,
  EMPTY,
  filter,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil
} from 'rxjs'
import {
  isMainOpenFinWindow,
  isOpenFinRuntime
} from '../../containers/OpenFin/helpers'
import { ErrorMessageContents, getHub } from '../../helpers/hub'
import { createInvokeEpic } from '../../signalr/createInvokeEpic'
import { createStreamEpic } from '../../signalr/createStreamEpic'
import { cancelOrders } from '../order/actions'
import { State as OrderState } from '../order/reducer'
import { getUserOrders } from '../order/selectors'
import { querySearch$ } from '../searchSecurities/epic'
import { createSecuritySearchResultFromResponse } from '../searchSecurities/helpers'
import {
  setIsMine,
  SetWatchListId,
  setWatchListId
} from '../securities/actions'
import { State as SecuritiesState } from '../securities/reducer'
import { getGridIndicesWithWatchlist } from '../securities/selectors'
import { removeAllStagedOrders } from '../stagedOrders/actions'
import {
  createWatchlistFromWatchlistInfo,
  logInvalidIdentifiers
} from '../upload/actions'
import { getSecurityIdsFromIdentifier$ } from '../upload/helpers'
import { ErrorInfo } from '../upload/types'
import { logError } from '../ws/actions'
import {
  AddSecuritiesToWatchlistAction,
  AppendIssuerToWatchlistAction,
  AppendSecurityToWatchlistAction,
  CancelAllWatchListOrdersAction,
  CheckOrUncheckAllSecuritiesAction,
  checkOrUncheckSecurities,
  clearFailedSymbolsAndCusipsFromWatchlistDefinitionUpdate,
  CreateWatchlistForUserAction,
  DeleteWatchlistAction,
  failedSymbolsAndCusipsFromWatchlistDefinitionUpdate,
  fetchAdminWatchListsSuccessAction,
  FetchBackgroundDetailsAction,
  fetchBackgroundDetailsSuccess,
  fetchWatchlistDetails,
  FetchWatchlistDetailsAction,
  fetchWatchlistDetailsFailure,
  fetchWatchlistDetailsSuccess,
  fetchWatchListsAction,
  FetchWatchListsFailureAction,
  HideWatchlistAction,
  RemoveCheckedSecuritiesFromWatchlistAction,
  RemoveSecurityFromWatchlistAction,
  resetCheckedSecurities,
  UpdateWatchlistDefinitionAction,
  UpdateWatchlistNameAction,
  watchlistDefinitionUpdated,
  WatchListsAction
} from './actions'
import {
  fetchAdminWatchlistsByIdentifierDefinition,
  fetchAdminWatchlistsByUserDefinition,
  fetchWatchListDefinition
} from './definition'
import {
  getResponseForPermission,
  watchlistDetailsFromResponse
} from './helpers'
import { State } from './reducer'
import {
  getCheckedSecuritiesStatus,
  getCheckedSecurityIds,
  getDetailsForCurrentWatchlist,
  getDetailsForWatchlist,
  getWatchlistDetails
} from './selectors'
import { WatchlistDetails, WatchListResponse } from './types'

type CombinedState = {
  watchList: State
  securities: SecuritiesState
  order: OrderState
}

const fetchWatchListEpicFailureEpic: Epic<WatchListsAction> = (action$) =>
  action$.pipe(
    ofType<FetchWatchListsFailureAction>('watchList.fetchWatchListsFailure'),
    map((action) => fetchWatchListsAction(action.payload.gridIndex))
  )

const cancelAllWatchListOrdersEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<CancelAllWatchListOrdersAction>(
      'watchList.cancelAllWatchListOrdersAction'
    ),
    /* The frame's global cancel-all is state-synced to every OpenFin window, so
       gate the all-orders branch to the main window — otherwise each popout
       holding pending orders fires its own duplicate CancelOrders. Per-watchlist
       cancels only do work where the watchlist's details live; web has one store. */
    filter(
      (action) =>
        !isOpenFinRuntime() ||
        action.payload.watchlistId !== undefined ||
        isMainOpenFinWindow()
    ),
    mergeMap((action) => {
      const pendingUserOrders = getUserOrders(state$.value).filter(
        (o) => o.status === 'pending'
      )
      // return of(cancelOrders(pendingUserOrders.map((order) => order.id)))
      const { watchlistId } = action.payload
      if (watchlistId === undefined) {
        return of(cancelOrders(pendingUserOrders.map((order) => order.id)))
      } else {
        const watchlistSecurityIds =
          getWatchlistDetails(state$.value)(watchlistId)?.securityIds ?? []
        const userOrdersToBeCancelled = pendingUserOrders.filter((o) =>
          watchlistSecurityIds.includes(o.securityId)
        )
        return of(
          cancelOrders(userOrdersToBeCancelled.map((order) => order.id))
        )
      }
    })
  )

const fetchWatchlistDetailsEpic: Epic<Action> = (action$) => {
  return action$.pipe(
    ofType('watchList.fetchWatchlistDetails'),
    switchMap((action: FetchWatchlistDetailsAction) => {
      const watchlistId = action.payload.watchlistId
      const gridIndex = action.payload.gridIndex
      return getHub()
        .stream<WatchlistDetails>('GetWatchlist', watchlistId)
        .pipe(
          map((details) =>
            fetchWatchlistDetailsSuccess(
              watchlistId,
              watchlistDetailsFromResponse(details)
            )
          ),
          /*
             When the watchlist is gone the server sends back an error, but
             getHub().stream retries it and then gives up quietly, so all this
             epic sees is a stream that finished without ever sending details.
           */
          defaultIfEmpty(fetchWatchlistDetailsFailure('')),
          catchError((error: ErrorMessageContents) =>
            of(
              setWatchListId(gridIndex, undefined),
              fetchWatchlistDetailsFailure(error.message ?? ''),
              logError(error)
            )
          )
        )
    })
  )
}

const removeSecuritiesFromWatchlistEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('watchlist.removeCheckedSecuritiesFromWatchlist'),
    mergeMap((action: RemoveCheckedSecuritiesFromWatchlistAction) => {
      const { watchlistId } = action.payload
      const watchlist = getWatchlistDetails(state$.value)(watchlistId)
      const checkedSecurities = getCheckedSecurityIds(state$.value)[watchlistId]
      const gridIndices = getGridIndicesWithWatchlist(state$.value)(watchlistId)
      return getHub()
        .invoke(
          'RemoveFromWatchlist',
          watchlist.id,
          Array.from(checkedSecurities)
        )
        .pipe(
          mergeMap(() =>
            from([
              resetCheckedSecurities(watchlistId),
              ...gridIndices.map((index) => setWatchListId(index, watchlistId))
            ])
          ),
          catchError((err) => of(logError(err)))
        )
    })
  )

const removeSecurityFromWatchlistEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('watchlist.removeSecurityFromWatchlist'),
    mergeMap((action: RemoveSecurityFromWatchlistAction) => {
      const { watchlistId, securityId } = action.payload
      const gridIndices = getGridIndicesWithWatchlist(state$.value)(watchlistId)
      return getHub()
        .invoke('RemoveFromWatchlist', watchlistId, [securityId])
        .pipe(
          mergeMap(() =>
            from([
              resetCheckedSecurities(watchlistId),
              ...gridIndices.map((index) => setWatchListId(index, watchlistId))
            ])
          ),
          catchError((err) => of(logError(err)))
        )
    })
  )

const updateSecuritiesInWatchlistEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('watchList.addSecuritiesToWatchlist'),
    mergeMap((action: AddSecuritiesToWatchlistAction) => {
      const { watchlistId } = action.payload
      const watchlist = getWatchlistDetails(state$.value)(watchlistId)
      let identifiers: string[] = []
      if (action.payload.identifiers) {
        identifiers = action.payload.identifiers
      }

      const watchlistDetails = getDetailsForWatchlist(state$.value)(watchlistId)
      const gridIndices = getGridIndicesWithWatchlist(state$.value)(watchlistId)
      return getHub()
        .invoke(
          'UpdateWatchlist',
          watchlist.id,
          watchlist.name,
          identifiers,
          getResponseForPermission(watchlist.permission),
          watchlist.book,
          watchlistDetails?.filter,
          watchlistDetails?.myFirmChecked,
          watchlistDetails?.size,
          watchlistDetails?.useAdvancedFilter,
          false
        )
        .pipe(
          mergeMap(() =>
            of(
              ...gridIndices.map((index) => setWatchListId(index, watchlistId))
            )
          ),
          catchError((err) => of(logError(err)))
        )
    })
  )

const updateWatchlistDefinitionEpic: Epic<Action, Action, CombinedState> = (
  action$
) =>
  action$.pipe(
    ofType('watchList.updateWatchlistDefinition'),
    mergeMap((action: UpdateWatchlistDefinitionAction) => {
      const { watchlist } = action.payload
      return getSecurityIdsFromIdentifier$(watchlist.symbolsAndCusips).pipe(
        map((securityIdsArray) =>
          securityIdsArray
            .map((arr, i) =>
              arr.length === 0 ? watchlist.symbolsAndCusips[i] : ''
            )
            .filter((symbol) => !!symbol)
        ),
        mergeMap((failedSymbols) => {
          if (failedSymbols.length) {
            return of(
              failedSymbolsAndCusipsFromWatchlistDefinitionUpdate(
                failedSymbols
              ),
              logInvalidIdentifiers(failedSymbols)
            )
          }
          return getHub()
            .invoke(
              'UpdateWatchlist',
              watchlist.id,
              watchlist.name,
              watchlist.symbolsAndCusips,
              getResponseForPermission(watchlist.permission),
              watchlist.book,
              watchlist.filter,
              watchlist.myFirmChecked,
              watchlist.size,
              watchlist.useAdvancedFilter,
              true
            )
            .pipe(
              mergeMap(() =>
                of(
                  watchlistDefinitionUpdated(watchlist),
                  clearFailedSymbolsAndCusipsFromWatchlistDefinitionUpdate()
                )
              ),
              catchError((err) => of(logError(err)))
            )
        }),
        catchError((err) => of(logError(err)))
      )
    })
  )

const appendSecuritiesToWatchlistEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('watchList.appendSecurityToWatchlist'),
    mergeMap((action: AppendSecurityToWatchlistAction) => {
      const { watchlistId } = action.payload
      const gridIndices = getGridIndicesWithWatchlist(state$.value)(watchlistId)
      return getHub()
        .invoke(
          'AppendSecurityToWatchlist',
          watchlistId,
          action.payload.securityIds
        )
        .pipe(
          mergeMap(() =>
            of(
              ...gridIndices.map((index) => setWatchListId(index, watchlistId))
            )
          ),
          catchError((err) => of(logError(err)))
        )
    })
  )

const appendIssuerToWatchlistEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('watchList.appendIssuerToWatchlist'),
    mergeMap((action: AppendIssuerToWatchlistAction) => {
      const { watchlistId } = action.payload
      const gridIndices = getGridIndicesWithWatchlist(state$.value)(watchlistId)
      return getHub()
        .invoke(
          'AppendIssuerToWatchlist',
          watchlistId,
          action.payload.issuer,
          action.payload.append
        )
        .pipe(
          mergeMap(() =>
            of(
              ...gridIndices.map((index) => setWatchListId(index, watchlistId))
            )
          ),
          catchError((err) => of(logError(err)))
        )
    })
  )

const fetchWatchlistDetailsWhenChangingWatchlist: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('securities.setWatchListId'),
    filter(
      (action: SetWatchListId) =>
        action.payload.watchlistId !== undefined &&
        action.payload.watchlistId !== 0 // WatchlistId = 0 means this is a 'Mine' filter
    ),
    map((action) =>
      fetchWatchlistDetails(
        action.payload.watchlistId as number,
        action.payload.gridIndex
      )
    )
  )

const checkOrUncheckAllSecuritiesEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<CheckOrUncheckAllSecuritiesAction>(
      'watchlist.checkOrUncheckAllSecurities'
    ),
    mergeMap((action) => {
      const { gridIndex } = action.payload
      const state = state$.value
      const watchlist = getDetailsForCurrentWatchlist(state)(gridIndex)
      if (!watchlist) return EMPTY
      const status = getCheckedSecuritiesStatus(state)(watchlist.id!)

      if (status === 'all' || status === 'some') {
        return of(resetCheckedSecurities(watchlist.id ?? 0))
      } else {
        return of(
          checkOrUncheckSecurities(
            watchlist.id ?? 0,
            watchlist.securityIds,
            true
          )
        )
      }
    })
  )

const deleteWatchlistEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<DeleteWatchlistAction>('watchlist.deleteWatchlist'),
    mergeMap((action) => {
      const watchlistId = action.payload
      const gridIndicesWithWatchlist = getGridIndicesWithWatchlist(
        state$.value
      )(watchlistId)
      const watchlistSecurityIds =
        getWatchlistDetails(state$.value)(watchlistId)?.securityIds ?? []
      const hasPendingOrders = getUserOrders(state$.value).some(
        (o) =>
          o.status === 'pending' && watchlistSecurityIds.includes(o.securityId)
      )
      const actions = gridIndicesWithWatchlist.length
        ? [
            removeAllStagedOrders(),
            ...gridIndicesWithWatchlist.map((gridIndex) =>
              setWatchListId(gridIndex, undefined)
            ),
            ...(!hasPendingOrders
              ? gridIndicesWithWatchlist.map((gridIndex) =>
                  setIsMine(gridIndex, false)
                )
              : [])
          ]
        : []
      return getHub()
        .invoke('DeleteWatchlist', action.payload)
        .pipe(
          mergeMap(() => from(actions)),
          // map(() => deleteWatchlistSuccess(action.payload)),
          catchError((err) => of(logError(err)))
        )
    })
  )
const hideWatchlistEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<HideWatchlistAction>('watchlist.hideWatchlist'),
    mergeMap((action) => {
      const watchlistId = action.payload
      const gridIndicesWithWatchlist = getGridIndicesWithWatchlist(
        state$.value
      )(watchlistId)
      return getHub()
        .invoke('HideWatchlist', action.payload)
        .pipe(
          mergeMap(() =>
            from(
              gridIndicesWithWatchlist.map((gridIndex) =>
                setWatchListId(gridIndex, undefined)
              )
            )
          ),
          catchError((err) => of(logError(err)))
        )
    })
  )

const updateWatchlistNameEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('watchList.updateWatchlistName'),
    mergeMap((action: UpdateWatchlistNameAction) => {
      const { watchlistId, newName } = action.payload
      const watchlist = getWatchlistDetails(state$.value)(watchlistId)
      const watchlistDetails = getDetailsForWatchlist(state$.value)(watchlistId)
      const gridIndices = getGridIndicesWithWatchlist(state$.value)(watchlistId)

      return getHub()
        .invoke(
          'UpdateWatchlist',
          watchlistId,
          newName,
          watchlist.symbolsAndCusips,
          getResponseForPermission(watchlist.permission),
          watchlist.book,
          watchlistDetails?.filter,
          watchlistDetails?.myFirmChecked,
          watchlistDetails?.size,
          watchlistDetails?.useAdvancedFilter,
          false
        )
        .pipe(
          mergeMap(() =>
            of(
              ...gridIndices.map((index) => setWatchListId(index, watchlistId))
            )
          ),
          catchError((err) => of(logError(err)))
        )
    })
  )

const fetchAdminWatchlistsEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('watchList.fetchAdminWatchLists'),
    mergeMap(() => {
      return getHub()
        .stream<WatchListResponse[]>('GetWatchlistsAdminView')
        .pipe(
          map((watchlists) =>
            fetchAdminWatchListsSuccessAction(
              watchlists.map(watchlistDetailsFromResponse)
            )
          ),
          catchError((err) => of(logError(err))),
          takeUntil(
            action$.pipe(
              ofType(
                'watchList.fetchAdminWatchlistsByUser',
                'watchList.fetchAdminWatchlistsByIdentifier',
                'watchList.fetchAdminWatchListsSuccess'
              )
            )
          )
        )
    })
  )

const fetchBackgroundDetails: Epic = (action$) =>
  action$.pipe(
    ofType<FetchBackgroundDetailsAction>('watchList.fetchBackgroundDetails'),
    switchMap(() => {
      const search$ = querySearch$('')
      return search$.pipe(
        map((securities) =>
          securities.length
            ? fetchBackgroundDetailsSuccess(
                securities.map(createSecuritySearchResultFromResponse)
              )
            : EMPTY
        ),
        catchError((err) => of(logError(err)))
      )
    })
  )

type SecIdsAccumulator = {
  identifiersToAdd: string[]
  errors: ErrorInfo[]
}
type PermissionResponse = 0 | 1
const createWatchlistForUserEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('watchList.createWatchlistForUser'),
    switchMap((action: CreateWatchlistForUserAction) => {
      const { watchlist, userId } = action.payload
      const identifiers = watchlist.symbolsAndCusips.map((s) => s.toUpperCase())
      return getSecurityIdsFromIdentifier$(identifiers).pipe(
        mergeMap((secIds) => {
          const { identifiersToAdd, errors } = secIds.reduce(
            (acc: SecIdsAccumulator, idArr, index) => {
              if (idArr.length) {
                acc.identifiersToAdd.push(identifiers[index])
              } else {
                acc.errors.push({
                  index,
                  identifier: identifiers[index],
                  errorIndex: index
                } as const)
              }
              return acc
            },
            { identifiersToAdd: [], errors: [] }
          )
          if (errors.length) {
            const failed = errors.map((e) => e.identifier)
            return of(
              failedSymbolsAndCusipsFromWatchlistDefinitionUpdate(failed),
              logInvalidIdentifiers(failed)
            )
          }
          const watchlistInfo = {
            gridIndex: -1,
            name: watchlist.name,
            permission: getResponseForPermission(
              watchlist.permission
            ) as PermissionResponse,
            filter: watchlist.filter,
            myFirmChecked: false,
            size: 0,
            useAdvancedFilter: false
          }
          return of(
            createWatchlistFromWatchlistInfo(
              watchlistInfo,
              identifiersToAdd,
              userId
            ),
            clearFailedSymbolsAndCusipsFromWatchlistDefinitionUpdate()
          )
        }),
        catchError((err) => of(logError(err)))
      )
    })
  )

export default combineEpics(
  createStreamEpic('watchList.fetchWatchLists', fetchWatchListDefinition),
  cancelAllWatchListOrdersEpic,
  fetchWatchListEpicFailureEpic,
  fetchWatchlistDetailsWhenChangingWatchlist,
  fetchWatchlistDetailsEpic,
  updateSecuritiesInWatchlistEpic,
  checkOrUncheckAllSecuritiesEpic,
  deleteWatchlistEpic,
  hideWatchlistEpic,
  appendSecuritiesToWatchlistEpic,
  updateWatchlistDefinitionEpic,
  appendIssuerToWatchlistEpic,
  updateWatchlistNameEpic,
  fetchAdminWatchlistsEpic,
  createInvokeEpic(
    'watchList.fetchAdminWatchlistsByUser',
    fetchAdminWatchlistsByUserDefinition
  ),
  createInvokeEpic(
    'watchList.fetchAdminWatchlistsByIdentifier',
    fetchAdminWatchlistsByIdentifierDefinition
  ),
  fetchBackgroundDetails,
  createWatchlistForUserEpic,
  removeSecuritiesFromWatchlistEpic,
  removeSecurityFromWatchlistEpic
)
